import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconExternalLink, IconHeadphones, IconRefresh, IconUpload, IconX } from '@tabler/icons-react';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth/useAuth';
import { SUPABASE_URL } from '../lib/env';

type BunnyFile = {
  name: string;
  path: string;
  size: number;
};

type BunnyListResponse = {
  ok?: boolean;
  error?: string;
  files?: BunnyFile[];
};

type BunnyUploadResponse = {
  ok?: boolean;
  error?: string;
  filename?: string;
};

function formatBytes(bytes: number): string {
  const kb = 1024;
  const mb = 1024 * kb;
  const gb = 1024 * mb;
  if (bytes >= gb) return `${(bytes / gb).toFixed(2)} GB`;
  if (bytes >= mb) return `${(bytes / mb).toFixed(1)} MB`;
  if (bytes >= kb) return `${(bytes / kb).toFixed(1)} KB`;
  return `${bytes} B`;
}

function sanitizeFilename(name: string): string {
  // Keep it simple; server does the strict validation.
  return name.trim().replace(/[/\\\\]+/g, '').replace(/\s+/g, ' ');
}

function playbackCdnUrl(filename: string): string {
  const clean = filename.trim().replace(/[/\\\\]+/g, '');
  const encoded = encodeURIComponent(clean);
  return `https://musicalkultur.b-cdn.net/playback/${encoded}`;
}

export function PlaybacksPage() {
  const qc = useQueryClient();
  const { session } = useAuth();

  useEffect(() => {
    document.title = 'Playbacks – Musical!Kultur Admin';
  }, []);

  const [file, setFile] = useState<File | null>(null);
  const [filename, setFilename] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);

  const xhrRef = useRef<XMLHttpRequest | null>(null);

  const listQ = useQuery({
    queryKey: ['bunny-playbacks'],
    queryFn: async (): Promise<BunnyFile[]> => {
      const token = session?.access_token;
      if (!token) throw new Error('Nicht eingeloggt.');

      const r = await fetch(`${SUPABASE_URL}/functions/v1/bunny-playbacks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = (await r.json().catch(() => ({}))) as BunnyListResponse;
      if (!r.ok || j.ok !== true) {
        throw new Error(j.error ?? `Fehler (${r.status})`);
      }
      return j.files ?? [];
    },
    enabled: !!session?.access_token,
  });

  const canUpload = !!file && !!filename.trim() && !busy;

  const sizeLabel = useMemo(() => (file ? formatBytes(file.size) : ''), [file]);
  const cdnUrl = useMemo(() => (filename.trim() ? playbackCdnUrl(filename) : ''), [filename]);

  function pickMp3() {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = '.mp3,audio/mpeg';
    inp.onchange = () => {
      const f = inp.files?.[0] ?? null;
      setFile(f);
      if (f) setFilename(sanitizeFilename(f.name));
    };
    inp.click();
  }

  function abortUpload() {
    try {
      xhrRef.current?.abort();
    } catch {
      // ignore
    } finally {
      xhrRef.current = null;
      setBusy(false);
    }
  }

  async function startUpload() {
    const token = session?.access_token;
    if (!token) {
      notifications.show({ color: 'red', title: 'Playbacks', message: 'Nicht eingeloggt.' });
      return;
    }
    if (!file) {
      notifications.show({ color: 'yellow', title: 'Playbacks', message: 'Bitte eine MP3 auswählen.' });
      return;
    }

    const safeName = sanitizeFilename(filename);
    if (!safeName.toLowerCase().endsWith('.mp3')) {
      notifications.show({ color: 'yellow', title: 'Playbacks', message: 'Dateiname muss auf .mp3 enden.' });
      return;
    }

    // Friendly hint for large files.
    if (file.size >= 250 * 1024 * 1024) {
      const ok = window.confirm(
        `Die Datei ist ${formatBytes(file.size)} groß. Upload kann länger dauern.\n\nFortfahren?`,
      );
      if (!ok) return;
    }

    setBusy(true);
    setProgress(0);
    setUploadedBytes(0);

    const url = `${SUPABASE_URL}/functions/v1/bunny-playbacks?filename=${encodeURIComponent(safeName)}`;
    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;

    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', file.type || 'audio/mpeg');

    xhr.upload.onprogress = (ev) => {
      if (!ev.lengthComputable) return;
      const p = ev.total > 0 ? ev.loaded / ev.total : 0;
      setProgress(Math.max(0, Math.min(1, p)));
      setUploadedBytes(ev.loaded);
    };

    xhr.onerror = () => {
      setBusy(false);
      notifications.show({ color: 'red', title: 'Upload', message: 'Netzwerkfehler beim Upload.' });
    };

    xhr.onload = async () => {
      setBusy(false);
      let j: BunnyUploadResponse = {};
      try {
        j = JSON.parse(xhr.responseText || '{}');
      } catch {
        // ignore
      }
      if (xhr.status >= 200 && xhr.status < 300 && j.ok === true) {
        notifications.show({ color: 'teal', title: 'Playbacks', message: 'Upload erfolgreich.' });
        setFile(null);
        setFilename('');
        setProgress(0);
        setUploadedBytes(0);
        await qc.invalidateQueries({ queryKey: ['bunny-playbacks'] });
        return;
      }
      notifications.show({
        color: 'red',
        title: 'Upload fehlgeschlagen',
        message: j.error ?? `Fehler (${xhr.status})`,
      });
    };

    xhr.send(file);
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Playbacks"
        description="MP3s in Bunny Storage /playback/ hochladen."
        loading={listQ.isLoading}
        actions={
          <Tooltip label="Neu laden" withArrow>
            <ActionIcon variant="subtle" color="gray" onClick={() => listQ.refetch()} disabled={busy}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        }
      />

      <Card
        radius="lg"
        p="md"
        style={{
          background: 'var(--app-surface)',
          border: '1px solid var(--app-border)',
        }}
      >
        <Stack gap="sm">
          <Group justify="space-between" align="flex-end">
            <Box>
              <Text c="var(--color-text)" fw={700}>
                Upload
              </Text>
              <Text c="dimmed" size="sm">
                Datei wählen, Dateiname prüfen, hochladen.
              </Text>
            </Box>
            {busy ? (
              <Button
                variant="light"
                color="red"
                leftSection={<IconX size={16} />}
                onClick={abortUpload}
              >
                Abbrechen
              </Button>
            ) : (
              <Button variant="light" color="gray" onClick={pickMp3}>
                MP3 auswählen
              </Button>
            )}
          </Group>

          <TextInput
            label="Dateiname (.mp3)"
            value={filename}
            onChange={(e) => setFilename(e.currentTarget.value)}
            placeholder="z.B. 01_Intro.mp3"
          />

          {file && (
            <Group gap="xs">
              <Badge variant="light" color="brand">
                {file.name}
              </Badge>
              <Badge variant="light" color="gray">
                {sizeLabel}
              </Badge>
            </Group>
          )}

          {cdnUrl && (
            <Text size="sm" c="dimmed">
              CDN-URL: <Text span c="var(--color-text)">{cdnUrl}</Text>
            </Text>
          )}

          {busy && (
            <Stack gap={6}>
              <Text size="sm" c="dimmed">
                Upload: {(progress * 100).toFixed(0)}% ({formatBytes(uploadedBytes)} /{' '}
                {formatBytes(file?.size ?? 0)})
              </Text>
              <Box
                style={{
                  height: 10,
                  borderRadius: 999,
                  overflow: 'hidden',
                  background: 'var(--color-surface-3)',
                }}
              >
                <Box
                  style={{
                    height: '100%',
                    width: `${Math.max(0, Math.min(100, progress * 100))}%`,
                    background: 'rgba(34, 211, 238, 0.85)',
                  }}
                />
              </Box>
            </Stack>
          )}

          <Group justify="flex-end">
            <Button
              leftSection={<IconUpload size={16} />}
              onClick={startUpload}
              loading={busy}
              disabled={!canUpload}
            >
              Hochladen
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card
        radius="lg"
        p="md"
        style={{
          background: 'var(--app-surface)',
          border: '1px solid var(--app-border)',
        }}
      >
        <Group justify="space-between" mb="sm">
          <Text c="var(--color-text)" fw={700}>
            Dateien in `/playback/`
          </Text>
          <Text c="dimmed" size="sm">
            {listQ.data ? `${listQ.data.length} Datei(en)` : ''}
          </Text>
        </Group>

        {listQ.isError ? (
          <EmptyState
            icon={<IconHeadphones size={48} />}
            title="Liste konnte nicht geladen werden"
            description="Bitte Seite neu laden oder Admin kontaktieren."
          />
        ) : (listQ.data ?? []).length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={<IconHeadphones size={48} />}
            title="Noch keine Playbacks"
            description="Lade oben die erste MP3-Datei hoch."
          />
        ) : (
          <ScrollArea h={420}>
            <Table withRowBorders={false} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Datei</Table.Th>
                  <Table.Th>Größe</Table.Th>
                  <Table.Th style={{ width: 80 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(listQ.data ?? []).map((f) => (
                  <Table.Tr key={f.name}>
                    <Table.Td>
                      <Text c="var(--color-text)" fw={600} size="sm">
                        {f.name}
                      </Text>
                      <Text c="dimmed" size="xs">
                        {playbackCdnUrl(f.name)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text c="dimmed" size="sm">
                        {formatBytes(f.size)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Öffnen" withArrow>
                        <ActionIcon
                          component="a"
                          href={playbackCdnUrl(f.name)}
                          target="_blank"
                          rel="noreferrer"
                          variant="subtle"
                          color="brand"
                        >
                          <IconExternalLink size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>
    </Stack>
  );
}
